import { apiLogger } from '@commander/logger';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  success: boolean;
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: unknown;
  endpoint?: string;
  method?: string;
  isNetworkError?: boolean;
  isTimeout?: boolean;
  isCancel?: boolean;
}

/**
 * ApiClient - HTTP client for making API requests
 * Uses dependency injection pattern - axios instance must be provided in constructor
 */
export class ApiClient {
  constructor(private readonly axiosInstance: AxiosInstance) {
    if (!axiosInstance) {
      throw new Error(
        'AxiosInstance is required. Please provide an axios instance in the constructor.'
      );
    }
  }

  private getAxios(): AxiosInstance {
    return this.axiosInstance;
  }

  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  private async request<T>(endpoint: string, config: AxiosRequestConfig = {}): Promise<T> {
    try {
      const axios = this.getAxios();
      const response = await axios.request<ApiResponse<T>>({
        url: endpoint,
        ...config,
      });

      return response.data as T;
    } catch (error: unknown) {
      // Type guard for axios errors
      const isAxiosError = (
        err: unknown
      ): err is { response?: any; request?: any; message: string; code?: string; config?: any } => {
        return typeof err === 'object' && err !== null && 'message' in err;
      };

      if (!isAxiosError(error)) {
        // Non-axios error (shouldn't happen, but handle it)
        apiLogger.error('Unexpected error in API request:', {
          endpoint,
          method: config.method,
          error,
        });
        const apiError: ApiError = {
          message: 'An unexpected error occurred',
          endpoint,
          method: config.method?.toUpperCase(),
        };
        throw apiError;
      }

      // Validation failures come back as { success: false, errors: [{ field, message }] }
      // with no top-level `message`. Surface those field messages instead of the
      // generic axios "Request failed with status code 400".
      const responseData = error.response?.data;
      const fieldErrorMessage = Array.isArray(responseData?.errors)
        ? responseData.errors
            .map((e: { message?: string }) => e?.message)
            .filter(Boolean)
            .join('; ')
        : undefined;

      // Construct detailed error object
      const apiError: ApiError = {
        message: responseData?.message || fieldErrorMessage || error.message || 'Request failed',
        code: error.code,
        status: error.response?.status,
        details: error.response?.data,
        endpoint,
        method: config.method?.toUpperCase(),
        // Detect specific error types
        isNetworkError: !error.response && Boolean(error.request),
        isTimeout: error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT',
        isCancel: error.code === 'ERR_CANCELED',
      };

      // Log errors (except cancelled requests)
      if (!apiError.isCancel) {
        if (apiError.isNetworkError) {
          apiLogger.warn('Network error:', { endpoint, method: config.method });
        } else if (apiError.isTimeout) {
          apiLogger.warn('Request timeout:', { endpoint, method: config.method });
        } else if (apiError.status && apiError.status >= 500) {
          apiLogger.error('Server error:', {
            endpoint,
            method: config.method,
            status: apiError.status,
            message: apiError.message,
          });
        } else if (apiError.status && apiError.status >= 400) {
          apiLogger.debug('Client error:', {
            endpoint,
            method: config.method,
            status: apiError.status,
            message: apiError.message,
          });
        } else {
          apiLogger.error('API error:', { endpoint, method: config.method, error: apiError });
        }
      }

      throw apiError;
    }
  }

  // GET request
  async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', ...config });
  }

  // POST request
  async post<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      data,
      ...config,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      data,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      data,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // GET request with custom config (for binary responses, etc.)
  async getWithConfig<T>(endpoint: string, config: AxiosRequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      ...config,
    });
  }

  // POST request with custom config (for binary responses, etc.)
  async postWithConfig<T>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      data,
      ...config,
    });
  }
}

/**
 * Factory function to create an ApiClient instance
 * @param axiosInstance - Configured axios instance with interceptors
 */
export function createApiClient(axiosInstance: AxiosInstance): ApiClient {
  return new ApiClient(axiosInstance);
}
